import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ScrollText, Building2, Sparkles } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { SITE_PAGES, LEGAL_ADMIN_PAGES } from "./AdminLayout";
import api from "../../lib/api";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [nereaUsage, setNereaUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/nerea/usage/today");
        if (!cancelled) setNereaUsage(data);
      } catch { /* card stays in placeholder state */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="max-w-5xl">
      <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Dashboard</div>
      <h1 className="font-display mt-3 text-3xl sm:text-5xl font-semibold tracking-tighter chrome-text">
        Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
      </h1>
      <p className="mt-4 text-[15px] text-zinc-400 leading-relaxed max-w-xl">
        Edit any page, any line, any moment. Your changes go live instantly.
      </p>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SITE_PAGES.map((p) => (
          <Link
            key={p.key}
            to={`/admin/pages/${p.key}`}
            data-testid={`dashboard-card-${p.key}`}
            className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.12] transition-colors p-5"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Page</div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="font-display text-xl text-white tracking-tight">{p.label}</div>
              <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </div>
          </Link>
        ))}

        {/* Legal aggregate card */}
        <Link
          to="/admin/legal"
          data-testid="dashboard-card-legal"
          className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.12] transition-colors p-5"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Section</div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <ScrollText className="h-4 w-4 text-[#00E5FF]" strokeWidth={1.5} />
              <div className="font-display text-xl text-white tracking-tight">Legal</div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
          </div>
          <div className="mt-3 text-[12px] text-zinc-500">
            {LEGAL_ADMIN_PAGES.length} policies · privacy, terms, AML, complaints…
          </div>
        </Link>

        {/* Merchants — Brief 2 sub-task 4 */}
        <Link
          to="/admin/merchants"
          data-testid="dashboard-card-merchants"
          className="group relative rounded-2xl border border-[#00E5FF]/20 bg-gradient-to-br from-[#00E5FF]/[0.04] to-transparent hover:border-[#00E5FF]/40 transition-colors p-5"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#00E5FF]">Operations</div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Building2 className="h-4 w-4 text-[#00E5FF]" strokeWidth={1.5} />
              <div className="font-display text-xl text-white tracking-tight">Merchants</div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
          </div>
          <div className="mt-3 text-[12px] text-zinc-500">
            Application status · gateway URL · audit log
          </div>
        </Link>

        {/* Nerea usage — Brief 3.1 */}
        <Link
          to="/admin/nerea/autonomy"
          data-testid="dashboard-card-nerea-usage"
          className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.12] transition-colors p-5"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">Nerea</div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Sparkles className="h-4 w-4 text-[#00E5FF]" strokeWidth={1.5} />
              <div className="font-display text-xl text-white tracking-tight">Today's usage</div>
            </div>
            <ArrowUpRight className="h-4 w-4 text-zinc-500 group-hover:text-[#00E5FF] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            <div data-testid="dashboard-nerea-spend">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Spend</div>
              <div className="mt-1 font-display text-2xl text-white">
                {nereaUsage ? `$${(nereaUsage.spend_usd ?? 0).toFixed(3)}` : "—"}
              </div>
              {nereaUsage?.spend_cap_usd ? (
                <div className="text-[10px] text-zinc-500 mt-0.5">cap ${nereaUsage.spend_cap_usd}</div>
              ) : null}
            </div>
            <div data-testid="dashboard-nerea-tokens">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Tokens</div>
              <div className="mt-1 font-display text-2xl text-white">
                {nereaUsage ? (nereaUsage.tokens ?? 0).toLocaleString() : "—"}
              </div>
            </div>
            <div data-testid="dashboard-nerea-messages">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">Messages</div>
              <div className="mt-1 font-display text-2xl text-white">
                {nereaUsage ? nereaUsage.messages ?? 0 : "—"}
              </div>
            </div>
          </div>
          {nereaUsage?.top_merchants?.length ? (
            <div className="mt-4 border-t border-white/[0.06] pt-3">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2">Top by tokens</div>
              <ul className="space-y-1">
                {nereaUsage.top_merchants.slice(0, 3).map((m) => (
                  <li key={m.merchant_id} className="flex items-center justify-between text-[11.5px]">
                    <span className="text-zinc-400 truncate font-mono">{m.merchant_id.slice(0, 18)}</span>
                    <span className="text-zinc-300 tabular-nums">{m.tokens.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Link>
      </div>
    </div>
  );
}
